import { useLocale } from '@/components/Link'
import useTenant from '@/hooks/useTenant'
import type { Currency } from '@caterdesk/utils--money'
import { getCurrencySymbol } from '@caterdesk/utils--money'

interface RichRatingProps {
  vendor: {
    rating: number | null
    totalRatings: number | null
    logo: string | null
    companyName: string
    keywords: string | null
    priceLevel: number | null
    currency: Currency
    permalink: string
    locations: Array<{ city: string | null }>
  } | null
}

export function RichRating(props: RichRatingProps) {
  const { vendor } = props
  const locale = useLocale()
  const tenant = useTenant()

  if (!vendor) {
    return null
  }

  const vendorUrl = `https://${tenant.domain}/${locale}/office-catering/vendors/${vendor.permalink}`

  const areaServed = vendor.locations
    .map((location) => location.city)
    .filter((city): city is string => Boolean(city))

  const uniqueCities = Array.from(new Set(areaServed))

  const restaurantSnippet = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    image: vendor.logo ? [vendor.logo] : [],
    name: vendor.companyName,

    // We only want to show this if there's been at least
    // a single rating added to the vendor.
    ...(vendor.rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: vendor.rating,
        bestRating: 5,
        ratingCount: vendor.totalRatings,
      },
    }),

    url: vendorUrl,
    servesCuisine: vendor.keywords?.split(/,\s*/) || [],
    ...(vendor.priceLevel != null && {
      priceRange: Array.from({ length: vendor.priceLevel || 1 })
        .map(() => getCurrencySymbol(vendor.currency))
        .join(''),
    }),
    menu: vendorUrl,
    address: uniqueCities.length > 0 ? uniqueCities : undefined,
    areaServed: uniqueCities.length > 0 ? uniqueCities : undefined,
  }

  return <script type="application/ld+json">{JSON.stringify(restaurantSnippet, null, 2)}</script>
}
